import React, { useState, useCallback } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ArrowRight, Check, Search } from 'lucide-react';

const PC_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

export const Step1ProjectDetails = ({ form, setForm, onContinue }) => {
  const [postcodeValid, setPostcodeValid] = useState(!!form.postcode && PC_RE.test(form.postcode));
  const [addressList, setAddressList] = useState([]);
  const [showAddressList, setShowAddressList] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(!!(form.addressLine1 && form.postcode));
  const [manualEntry, setManualEntry] = useState(false);

  const canContinue = form.clientName && form.email && form.phone && 
                      form.addressLine1 && form.city && form.postcode;

  const handlePostcodeChange = (val) => {
    const upper = val.toUpperCase();
    setForm({ ...form, postcode: upper });
    setPostcodeValid(PC_RE.test(upper));
    if (!PC_RE.test(upper)) {
      setAddressConfirmed(false);
      setShowAddressList(false);
    }
  };

  const lookupAddresses = useCallback(async () => {
    if (!postcodeValid) return;
    const pc = form.postcode;
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${API}/address/lookup?postcode=${encodeURIComponent(pc)}`);
      const data = await res.json();
      if (data.found && data.addresses?.length) {
        setAddressList(data.addresses);
      } else {
        setAddressList([{ line1: pc, city: '', postcode: pc }]);
      }
    } catch {
      setAddressList([{ line1: pc, city: '', postcode: pc }]);
    }
    setShowAddressList(true);
  }, [form.postcode, postcodeValid]);

  const selectAddress = (addr) => {
    setForm({
      ...form,
      addressLine1: addr.line1,
      city: addr.city,
      postcode: addr.postcode,
    });
    setAddressConfirmed(true);
    setShowAddressList(false);
  };

  const resetAddress = () => {
    setAddressConfirmed(false);
    setShowAddressList(false);
    setManualEntry(false);
    setForm({ ...form, addressLine1: '', addressLine2: '', city: '', county: '' });
  };

  const enterManually = () => {
    setManualEntry(true);
    setShowAddressList(false);
  };

  return (
    <div className="card" data-testid="step-1-card">
      <h2 className="text-2xl font-semibold mb-6" style={{ color: 'var(--color-navy)' }}>
        Project Details
      </h2>
      
      <div className="space-y-5">
        {/* Client Info */}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="input-label">Your Name *</Label>
            <Input
              className="input-styled"
              data-testid="client-name-input"
              value={form.clientName}
              onChange={(e) => setForm({ ...form, clientName: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="input-label">Email *</Label>
            <Input
              className="input-styled"
              type="email"
              data-testid="email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="input-label">Phone *</Label>
            <Input
              className="input-styled"
              type="tel"
              data-testid="phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="input-label">Project Type *</Label>
            <Select
              value={form.projectType}
              onValueChange={(value) => setForm({ ...form, projectType: value })}
            >
              <SelectTrigger className="input-styled" data-testid="project-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="refurbishment">Refurbishment</SelectItem>
                <SelectItem value="hmo_conversion">HMO Conversion</SelectItem>
                <SelectItem value="flat_conversion">Flat Conversion</SelectItem>
                <SelectItem value="extension">Extension</SelectItem>
                <SelectItem value="loft_conversion">Loft Conversion</SelectItem>
                <SelectItem value="commercial_to_resi">Commercial to Residential</SelectItem>
                <SelectItem value="new_build">New Build</SelectItem>
                <SelectItem value="commercial_large">Commercial (Large)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Smart Project Address */}
        <div className="space-y-4 pt-2">
          <Label className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>
            Project Address *
          </Label>

          {/* Postcode Entry */}
          {!addressConfirmed && !manualEntry && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="input-label">Postcode *</Label>
                <div className="relative">
                  <Input
                    className="input-styled uppercase"
                    data-testid="address-postcode"
                    placeholder="e.g. M21 9WQ"
                    value={form.postcode}
                    onChange={(e) => handlePostcodeChange(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && lookupAddresses()}
                  />
                  {postcodeValid && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#16A34A' }}>
                      <Check className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>

              {postcodeValid && !showAddressList && (
                <button
                  type="button"
                  onClick={lookupAddresses}
                  className="btn-primary flex items-center gap-2"
                  style={{ width: '100%', padding: '12px', fontSize: 13 }}
                  data-testid="find-address-btn"
                >
                  <Search className="w-4 h-4" /> Find address
                </button>
              )}

              {/* Address List */}
              {showAddressList && (
                <div className="space-y-2">
                  <Label className="input-label">Select your property</Label>
                  <div style={{
                    maxHeight: 220, overflowY: 'auto',
                    border: '2px solid var(--color-navy)',
                    borderRadius: 10,
                    background: '#fff',
                  }} data-testid="address-list">
                    {addressList.map((a, i) => (
                      <button
                        key={a.full || a.line1 || i}
                        type="button"
                        onClick={() => selectAddress(a)}
                        data-testid={`addr-opt-${i}`}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '10px 14px', fontSize: 13,
                          cursor: 'pointer', color: '#1E293B',
                          border: 'none', borderBottom: '1px solid #E2E8F0',
                          background: '#fff', fontFamily: 'inherit',
                          transition: 'background .1s',
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#F0F4FF'}
                        onMouseLeave={(e) => e.target.style.background = '#fff'}
                      >
                        <div style={{ fontWeight: 600 }}>{a.line1}</div>
                        <div style={{ fontSize: 11, color: '#64748B', marginTop: 1 }}>
                          {a.city ? `${a.city}, ` : ''}{a.postcode}
                        </div>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={enterManually}
                    style={{
                      background: 'none', border: 'none', fontSize: 12,
                      color: '#64748B', cursor: 'pointer', fontFamily: 'inherit',
                      textDecoration: 'underline', padding: 0, marginTop: 4,
                    }}
                    data-testid="enter-manually-btn"
                  >
                    Can't find your address? Enter manually
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Address Confirmed */}
          {addressConfirmed && !manualEntry && (
            <div style={{
              background: '#F0FDF4', border: '1.5px solid #86EFAC',
              borderRadius: 10, padding: '14px 16px',
            }} data-testid="address-confirmed">
              <div style={{ fontSize: 10, fontWeight: 700, color: '#15803D', letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 4 }}>
                Property confirmed
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F2742' }}>{form.addressLine1}</div>
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                {form.city ? `${form.city}, ` : ''}{form.postcode}
              </div>
              <button
                type="button"
                onClick={resetAddress}
                style={{
                  background: 'none', border: 'none', fontSize: 11,
                  color: '#64748B', cursor: 'pointer', fontFamily: 'inherit',
                  textDecoration: 'underline', padding: 0, marginTop: 8,
                }}
                data-testid="change-address-btn"
              >
                Change address
              </button>
            </div>
          )}

          {/* Manual Entry Fallback */}
          {manualEntry && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label className="input-label">Address Line 1 *</Label>
                <Input
                  className="input-styled"
                  data-testid="address-line1"
                  placeholder="e.g. 123 High Street"
                  value={form.addressLine1}
                  onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="input-label">Address Line 2</Label>
                <Input
                  className="input-styled"
                  data-testid="address-line2"
                  placeholder="e.g. Flat 2, Building Name (optional)"
                  value={form.addressLine2}
                  onChange={(e) => setForm({ ...form, addressLine2: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="input-label">Town / City *</Label>
                <Input
                  className="input-styled"
                  data-testid="address-city"
                  placeholder="e.g. London"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="input-label">County</Label>
                <Input
                  className="input-styled"
                  data-testid="address-county"
                  placeholder="e.g. Greater London (optional)"
                  value={form.county}
                  onChange={(e) => setForm({ ...form, county: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="input-label">Postcode *</Label>
                <Input
                  className="input-styled uppercase"
                  data-testid="address-postcode-manual"
                  placeholder="e.g. SW1A 1AA"
                  value={form.postcode}
                  onChange={(e) => setForm({ ...form, postcode: e.target.value.toUpperCase() })}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="button"
                  onClick={() => { setManualEntry(false); setAddressConfirmed(false); }}
                  style={{
                    background: 'none', border: 'none', fontSize: 11,
                    color: '#64748B', cursor: 'pointer', fontFamily: 'inherit',
                    textDecoration: 'underline', padding: 0,
                  }}
                >
                  Use postcode lookup instead
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Property Details */}
        <div className="grid md:grid-cols-3 gap-5">
          <div className="space-y-2">
            <Label className="input-label">Floor Area (m²) *</Label>
            <Input
              className="input-styled"
              type="number"
              data-testid="gifa-input"
              value={form.gifaM2}
              onChange={(e) => setForm({ ...form, gifaM2: parseInt(e.target.value) || 0 })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="input-label">Number of Floors *</Label>
            <Input
              className="input-styled"
              type="number"
              data-testid="floors-input"
              value={form.floors}
              onChange={(e) => setForm({ ...form, floors: parseInt(e.target.value) || 1 })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="input-label">Number of Units *</Label>
            <Input
              className="input-styled"
              type="number"
              data-testid="units-input"
              value={form.units}
              onChange={(e) => setForm({ ...form, units: parseInt(e.target.value) || 1 })}
              required
            />
          </div>
        </div>

        {/* Information Level & Complexity */}
        <div className="space-y-4 pt-2">
          <Label className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>
            Project Complexity
          </Label>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="input-label">Information Level *</Label>
              <Select
                value={form.infoLevel}
                onValueChange={(value) => setForm({ ...form, infoLevel: value })}
              >
                <SelectTrigger className="input-styled" data-testid="info-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="photos_only">Photos Only</SelectItem>
                  <SelectItem value="scaled_drawings">Scaled Drawings</SelectItem>
                  <SelectItem value="architect_drawings">Architect Drawings</SelectItem>
                  <SelectItem value="full_tender_pack">Full Tender Pack</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="drainageChanges"
                checked={form.drainageChanges}
                onCheckedChange={(checked) => setForm({ ...form, drainageChanges: checked })}
              />
              <Label htmlFor="drainageChanges" className="text-sm cursor-pointer" style={{ color: 'var(--color-text)' }}>
                Drainage changes
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="fullStripOut"
                checked={form.fullStripOut}
                onCheckedChange={(checked) => setForm({ ...form, fullStripOut: checked })}
              />
              <Label htmlFor="fullStripOut" className="text-sm cursor-pointer" style={{ color: 'var(--color-text)' }}>
                Full strip out
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="mixedUse"
                checked={form.mixedUse}
                onCheckedChange={(checked) => setForm({ ...form, mixedUse: checked })}
              />
              <Label htmlFor="mixedUse" className="text-sm cursor-pointer" style={{ color: 'var(--color-text)' }}>
                Mixed use property
              </Label>
            </div>
          </div>
        </div>

        {/* Listed Building Checkbox */}
        <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-start space-x-3">
            <Checkbox
              id="isListedBuilding"
              data-testid="listed-building-checkbox"
              checked={form.isListedBuilding}
              onCheckedChange={(checked) => setForm({ ...form, isListedBuilding: checked })}
              className="mt-1"
            />
            <div>
              <Label htmlFor="isListedBuilding" className="font-semibold cursor-pointer" style={{ color: 'var(--color-text)' }}>
                This is a Listed Building
              </Label>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>
                Tick this box if the property is a Grade I, Grade II*, or Grade II listed building.
              </p>
            </div>
          </div>
        </div>

        {/* Listed Building Warning */}
        {form.isListedBuilding && (
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F59E0B' }}>
                <span className="text-white text-sm font-bold">!</span>
              </div>
              <div>
                <p className="font-semibold" style={{ color: '#92400E' }}>Listed Building - Internal Review Required</p>
                <p className="text-sm mt-1" style={{ color: '#A16207' }}>
                  As this is a listed building, your submission will undergo an internal review before work commences.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Continue Button */}
        <div className="flex justify-end pt-4">
          <button
            onClick={onContinue}
            disabled={!canContinue}
            className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="step1-continue-btn"
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
